import api from "../config/api";
import type { ApiResponse, PaginatedResponse } from "../types";

// ==================== TYPES ====================

// ServiceCharge Types
export interface ServiceCharge {
  _id: string;
  userId: {
    _id: string;
    name: string;
    email: string;
    phone?: string;
  };
  serviceId: {
    _id: string;
    name: string;
    price: number;
  };
  type: 'service' | 'recommend';
  price: number;
  date: string; // ISO date string
  note?: string;
  createdBy: {
    _id: string;
    name: string;
    email: string;
  };
  isDeleted: boolean;
  deletedAt?: string;
  deletedBy?: string;
  createdAt: string;
  updatedAt: string;
  // Virtuals
  isToday?: boolean;
  isThisMonth?: boolean;
}

// Request Interfaces
export interface CreateServiceChargeRequest {
  userId: string;
  serviceId: string;
  type: 'service' | 'recommend';
  price: number;
  date: string; // ISO date string
  note?: string;
}

export interface UpdateServiceChargeRequest extends Partial<CreateServiceChargeRequest> {}

export interface GetServiceChargesParams {
  page?: number;
  limit?: number;
  userId?: string;
  serviceId?: string;
  type?: 'service' | 'recommend';
  startDate?: string;
  endDate?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Report Types
export interface DailySummaryItem {
  _id: {
    userId: string;
    type: 'service' | 'recommend';
  };
  total: number;
  count: number;
  user: {
    _id: string;
    name: string;
    email: string;
  };
}

export interface DailySummaryResponse {
  date: string;
  summary: DailySummaryItem[];
  totals: {
    service: number;
    recommend: number;
    all: number;
  };
}

export interface MonthlyReportItem {
  _id: {
    userId: string;
    type: 'service' | 'recommend';
  };
  total: number;
  count: number;
  user: {
    _id: string;
    name: string;
    email: string;
  };
}

export interface MonthlyReportResponse {
  year: number;
  month: number;
  report: MonthlyReportItem[];
  totals: {
    service: number;
    recommend: number;
    all: number;
  };
}

export interface DateRangeReportItem {
  _id: {
    date: string;
    type: 'service' | 'recommend';
  };
  total: number;
  count: number;
}

export interface DateRangeReportResponse {
  startDate: string;
  endDate: string;
  filters?: {
    userId?: string;
    serviceId?: string;
    type?: 'service' | 'recommend';
  };
  report: DateRangeReportItem[];
  totals: {
    service: number;
    recommend: number;
    all: number;
  };
}

export interface TopPerformerItem {
  _id: string;
  totalRevenue: number;
  serviceCount: number;
  user: {
    _id: string;
    name: string;
    email: string;
    phone?: string;
  };
}

export interface TopPerformersResponse {
  startDate: string;
  endDate: string;
  limit: number;
  topPerformers: TopPerformerItem[];
}

export interface RevenueByServiceItem {
  _id: string;
  total: number;
  count: number;
  service: {
    _id: string;
    name: string;
    price: number;
    status: string;
  };
}

export interface RevenueByServiceResponse {
  startDate: string;
  endDate: string;
  revenue: RevenueByServiceItem[];
  total: number;
}

export interface UserSummaryItem {
  _id: 'service' | 'recommend';
  total: number;
  count: number;
}

export interface UserSummaryResponse {
  userId: string;
  startDate: string;
  endDate: string;
  summary: UserSummaryItem[];
  total: number;
  count: number;
}

export interface GetDailySummaryParams {
  date: string; // ISO date string (YYYY-MM-DD)
}

export interface GetMonthlyReportParams {
  year: number;
  month: number; // 1-12
}

export interface GetDateRangeReportParams {
  startDate: string; // ISO date string
  endDate: string; // ISO date string
  userId?: string;
  serviceId?: string;
  type?: 'service' | 'recommend';
}

export interface GetTopPerformersParams {
  startDate: string;
  endDate: string;
  limit?: number;
  type?: 'service' | 'recommend';
}

export interface GetRevenueByServiceParams {
  startDate: string;
  endDate: string;
  type?: 'service' | 'recommend';
}

export interface GetUserSummaryParams {
  startDate: string;
  endDate: string;
}

// ==================== SERVICE ====================

export const serviceChargeService = {
  // ==================== CRUD OPERATIONS ====================

  /**
   * ດຶງລາຍການ Service Charges ທັງໝົດ
   * @param params - Query parameters for filtering and pagination
   * @returns Paginated list of service charges
   */
  getServiceCharges: async (
    params: GetServiceChargesParams = {}
  ): Promise<PaginatedResponse<ServiceCharge>> => {
    const response = await api.get('/service-charges', { params });
    return response.data;
  },

  /**
   * ດຶງຂໍ້ມູນ Service Charge ຏາມ ID
   * @param id - Service charge ID
   * @returns Service charge details
   */
  getServiceCharge: async (id: string): Promise<ApiResponse<ServiceCharge>> => {
    const response = await api.get(`/service-charges/${id}`);
    return response.data;
  },

  /**
   * ສ້າງ Service Charge ໃໝ່
   * @param data - Service charge data
   * @returns Created service charge
   */
  createServiceCharge: async (
    data: CreateServiceChargeRequest
  ): Promise<ApiResponse<ServiceCharge>> => {
    const response = await api.post('/service-charges', data);
    return response.data;
  },

  /**
   * ແກ້ໄຂຂໍ້ມູນ Service Charge
   * @param id - Service charge ID
   * @param data - Updated data
   * @returns Updated service charge
   */
  updateServiceCharge: async (
    id: string,
    data: UpdateServiceChargeRequest
  ): Promise<ApiResponse<ServiceCharge>> => {
    const response = await api.put(`/service-charges/${id}`, data);
    return response.data;
  },

  /**
   * ລຶບ Service Charge (Soft Delete)
   * @param id - Service charge ID
   * @returns Deleted service charge
   */
  deleteServiceCharge: async (id: string): Promise<ApiResponse<null>> => {
    const response = await api.delete(`/service-charges/${id}`);
    return response.data;
  },

  /**
   * ກູ້ຄືນຂໍ້ມູນ Service Charge
   * @param id - Service charge ID
   * @returns Restored service charge
   */
  restoreServiceCharge: async (id: string): Promise<ApiResponse<ServiceCharge>> => {
    const response = await api.patch(`/service-charges/${id}/restore`);
    return response.data;
  },

  // ==================== REPORT OPERATIONS ====================

  /**
   * ສະຫຼຸບລາຍຮັບລາຍວັນ
   * @param params - Date parameter
   * @returns Daily summary
   */
  getDailySummary: async (
    params: GetDailySummaryParams
  ): Promise<ApiResponse<DailySummaryResponse>> => {
    const response = await api.get('/service-charges/reports/daily', { params });
    return response.data;
  },

  /**
   * ລາຍງານລາຍເດືອນ
   * @param params - Year and month parameters
   * @returns Monthly report
   */
  getMonthlyReport: async (
    params: GetMonthlyReportParams
  ): Promise<ApiResponse<MonthlyReportResponse>> => {
    const response = await api.get('/service-charges/reports/monthly', { params });
    return response.data;
  },

  /**
   * ລາຍງານຕາມຊ່ວງວັນທີ່
   * @param params - Date range and filter parameters
   * @returns Date range report
   */
  getDateRangeReport: async (
    params: GetDateRangeReportParams
  ): Promise<ApiResponse<DateRangeReportResponse>> => {
    const response = await api.get('/service-charges/reports/date-range', { params });
    return response.data;
  },

  /**
   * ຫາພະນັກງານທີ່ມີຜົນງານດີທີ່ສຸດ
   * @param params - Date range and limit parameters
   * @returns Top performers
   */
  getTopPerformers: async (
    params: GetTopPerformersParams
  ): Promise<ApiResponse<TopPerformersResponse>> => {
    const response = await api.get('/service-charges/reports/top-performers', { params });
    return response.data;
  },

  /**
   * ລາຍງານລາຍຮັບຕາມບໍລິການ
   * @param params - Date range parameters
   * @returns Revenue by service
   */
  getRevenueByService: async (
    params: GetRevenueByServiceParams
  ): Promise<ApiResponse<RevenueByServiceResponse>> => {
    const response = await api.get('/service-charges/reports/by-service', { params });
    return response.data;
  },

  /**
   * ສະຫຼຸບລາຍຮັບຂອງພະນັກງານ
   * @param userId - User ID
   * @param params - Date range parameters
   * @returns User summary
   */
  getUserSummary: async (
    userId: string,
    params: GetUserSummaryParams
  ): Promise<ApiResponse<UserSummaryResponse>> => {
    const response = await api.get(`/service-charges/reports/user-summary/${userId}`, { params });
    return response.data;
  },

  // ==================== HELPER METHODS ====================

  /**
   * ດຶງລາຍການສຳລັບວັນນີ້
   * @returns Service charges for today
   */
  getTodayCharges: async (): Promise<PaginatedResponse<ServiceCharge>> => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endOfDay = new Date();
    endOfDay.setHours(23, 59, 59, 999);

    return serviceChargeService.getServiceCharges({
      startDate: today.toISOString(),
      endDate: endOfDay.toISOString(),
      page: 1,
      limit: 100
    });
  },

  /**
   * ດຶງລາຍການຂອງເດືອນນີ້
   * @returns Service charges for current month
   */
  getThisMonthCharges: async (): Promise<PaginatedResponse<ServiceCharge>> => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);

    return serviceChargeService.getServiceCharges({
      startDate: firstDay.toISOString(),
      endDate: lastDay.toISOString(),
      page: 1,
      limit: 1000
    });
  },

  /**
   * ດຶງລາຍການຂອງພະນັກງານໃນວັນນີ້
   * @param userId - User ID
   * @returns Service charges for user today
   */
  getUserTodayCharges: async (userId: string): Promise<PaginatedResponse<ServiceCharge>> => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endOfDay = new Date();
    endOfDay.setHours(23, 59, 59, 999);

    return serviceChargeService.getServiceCharges({
      userId,
      startDate: today.toISOString(),
      endDate: endOfDay.toISOString(),
      page: 1,
      limit: 100
    });
  },

  /**
   * ດຶງລາຍການຂອງບໍລິການໃນວັນນີ້
   * @param serviceId - Service ID
   * @returns Service charges for service today
   */
  getServiceTodayCharges: async (serviceId: string): Promise<PaginatedResponse<ServiceCharge>> => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endOfDay = new Date();
    endOfDay.setHours(23, 59, 59, 999);

    return serviceChargeService.getServiceCharges({
      serviceId,
      startDate: today.toISOString(),
      endDate: endOfDay.toISOString(),
      page: 1,
      limit: 100
    });
  },

  /**
   * ສະຫຼຸບລາຍຮັບວັນນີ້
   * @returns Today's summary
   */
  getTodaySummary: async (): Promise<ApiResponse<DailySummaryResponse>> => {
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0]; // YYYY-MM-DD

    return serviceChargeService.getDailySummary({ date: dateStr });
  },

  /**
   * ລາຍງານເດືອນນີ້
   * @returns This month's report
   */
  getThisMonthReport: async (): Promise<ApiResponse<MonthlyReportResponse>> => {
    const now = new Date();
    return serviceChargeService.getMonthlyReport({
      year: now.getFullYear(),
      month: now.getMonth() + 1
    });
  },

  /**
   * ສະຫຼຸບລາຍຮັບພະນັກງານເດືອນນີ້
   * @param userId - User ID
   * @returns User's monthly summary
   */
  getUserThisMonthSummary: async (userId: string): Promise<ApiResponse<UserSummaryResponse>> => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    return serviceChargeService.getUserSummary(userId, {
      startDate: firstDay.toISOString().split('T')[0],
      endDate: lastDay.toISOString().split('T')[0]
    });
  }
};